import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Article, TeamMember } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection } from 'firebase/firestore';
import RichTextEditor from '../../components/RichTextEditor';
import { useToast } from '../../hooks/useToast';
import { GoogleGenAI } from '@google/genai';
import { useCollection } from '../../hooks/useCollection';

interface AdminArticleFormPageProps {
}

const generateSlug = (title: string) => {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
};

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;


const ImageUploader: React.FC<{ label: string; value: string; onChange: (value: string) => void; }> = ({ label, value, onChange }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { showToast } = useToast();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const storageRef = ref(storage, `articles/${file.name}_${Date.now()}`);
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                onChange(downloadURL);
            } catch (error: any) {
                console.error("Upload failed:", error);
                let message = "Upload failed. Please try again.";
                if (error.code === 'storage/unauthorized') {
                    message = 'Permission Denied: Please check your Firebase Storage rules to allow write access.';
                }
                showToast(message, { type: 'error' });
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div 
                className="relative w-full h-48 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 transition-colors bg-gray-50"
                onClick={() => !isUploading && inputRef.current?.click()}
            >
                 {isUploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md z-10">
                        <div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div>
                    </div>
                )}
                {value ? (
                    <img src={value} alt={label} className="max-h-full max-w-full object-contain p-1" />
                ) : (
                    <div className="text-center text-gray-500">
                        <UploadIcon className="mx-auto h-8 w-8"/>
                        <span className="mt-2 text-sm">Click to upload</span>
                    </div>
                )}
            </div>
            <input 
                type="file"
                ref={inputRef}
                className="hidden"
                accept="image/png, image/jpeg, image/gif, image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
            />
        </div>
    );
};


const AdminArticleFormPage: React.FC<AdminArticleFormPageProps> = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [article, setArticle] = useState<Omit<Article, 'id'>>({
        title: '',
        slug: '',
        excerpt: '',
        imageUrl: '',
        author: { id: '', name: '', avatarUrl: '' },
        publishDate: new Date().toISOString().split('T')[0],
        content: '',
        published: true,
    });
    
    const { data: teamMembers } = useCollection<TeamMember>('teamMembers');

    const [imagePrompt, setImagePrompt] = useState('');
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [generatedImage, setGeneratedImage] = useState<string | null>(null); // base64 string
    const [isUploadingGenerated, setIsUploadingGenerated] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGeneratingArticle, setIsGeneratingArticle] = useState(false);


    useEffect(() => {
        if (isEditing && id) {
            const fetchArticle = async () => {
                const docRef = doc(db, 'articles', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setArticle(docSnap.data() as Article);
                } else {
                    showToast('Article not found.', { type: 'error' });
                    navigate('/admin/journal');
                }
                setIsLoading(false);
            }
            fetchArticle();
        } else {
            setIsLoading(false);
        }
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        
        if (type === 'checkbox') {
            setArticle(prev => ({...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setArticle(prev => ({ ...prev, [name]: value }));
            if (name === 'title' && !isEditing) {
                setArticle(prev => ({...prev, slug: generateSlug(value)}));
            }
        }
    };
    
    const handleAuthorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const authorId = e.target.value;
        const selectedMember = teamMembers?.find(m => m.id === authorId);
        if (selectedMember) {
            setArticle(prev => ({
                ...prev,
                author: {
                    id: selectedMember.id,
                    name: selectedMember.name,
                    avatarUrl: selectedMember.imageUrl,
                }
            }));
        }
    };

    const handleGenerateImage = async () => {
        if (!imagePrompt.trim()) {
            showToast("Please enter a prompt to generate an image.", { type: 'error' });
            return;
        }
        setIsGeneratingImage(true);
        setGeneratedImage(null);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const response = await ai.models.generateImages({
                model: 'imagen-4.0-generate-001',
                prompt: `Professional architectural photography of ${imagePrompt}. High-end, minimalist, clean lighting.`,
                config: {
                    numberOfImages: 1,
                    outputMimeType: 'image/jpeg',
                    aspectRatio: '16:9',
                },
            });
            const base64ImageBytes = response.generatedImages[0].image.imageBytes;
            setGeneratedImage(base64ImageBytes);
            showToast("Image generated successfully!", { type: 'success' });
        } catch (error) {
            console.error("AI image generation failed:", error);
            showToast("Sorry, there was an error generating the image.", { type: 'error' });
        } finally {
            setIsGeneratingImage(false);
        }
    };
    
    const handleUseGeneratedImage = async () => {
        if (!generatedImage) return;
        setIsUploadingGenerated(true);
        try {
            const byteCharacters = atob(generatedImage);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            const storageRef = ref(storage, `articles/ai_generated_${Date.now()}.jpeg`);
            await uploadBytes(storageRef, blob);
            const downloadURL = await getDownloadURL(storageRef);
            
            setArticle(prev => ({ ...prev, imageUrl: downloadURL }));
            setGeneratedImage(null);
            showToast("AI image has been set as the feature image.", { type: 'success' });
        } catch (error) {
            showToast("Failed to save the generated image.", { type: 'error' });
        } finally {
            setIsUploadingGenerated(false);
        }
    };

    const handleGenerateArticle = async () => {
        if (!aiPrompt.trim()) {
            showToast("Please enter some keywords or an outline.", { type: 'error' });
            return;
        }
        setIsGeneratingArticle(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `As an expert writer on architecture and design, write a full journal article based on the following keywords/outline: "${aiPrompt}".
            The article should have a clear structure with an introduction, body, and conclusion.
            The response MUST be in well-formed HTML format. Use an <h1> tag for the main title, <h2> or <h3> for subheadings, and <p> tags for paragraphs. Use <strong> for emphasis where appropriate.
            The tone should be insightful, professional, and engaging for an audience interested in architecture.`;

            const response = await ai.models.generateContent({
                // FIX: Update model to recommended model for text generation.
                model: 'gemini-3-flash-preview',
                contents: prompt,
            });
            const text = response.text.trim();
            if (text) {
                // Extract title from h1 tag if it exists
                const titleMatch = text.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
                let content = text;
                let extractedTitle = article.title;
                if (titleMatch && titleMatch[1]) {
                    extractedTitle = titleMatch[1].trim();
                    // Remove the h1 tag from the content to avoid duplicate titles
                    content = text.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '').trim();
                }

                setArticle(prev => ({ ...prev, title: extractedTitle, slug: generateSlug(extractedTitle), content }));
                
                // Generate excerpt from the full content
                try {
                    const excerptPrompt = `Concisely summarize the following article into an engaging excerpt of about 25-35 words, suitable for a preview card. Here is the article:\n\n${text}`;
                    const excerptResponse = await ai.models.generateContent({
                        // FIX: Update model to recommended model for text generation.
                        model: 'gemini-3-flash-preview',
                        contents: excerptPrompt,
                    });
                    const excerptText = excerptResponse.text.trim().replace(/"/g, '');
                    if (excerptText) {
                        setArticle(prev => ({ ...prev, excerpt: excerptText }));
                    }
                } catch (excerptError) {
                    console.error("Could not generate excerpt:", excerptError);
                    showToast("Article content generated, but failed to create excerpt.", { type: 'info' });
                }

                showToast('Article generated successfully!', { type: 'success' });
            } else {
                 throw new Error("AI did not return any content.");
            }
        } catch (error) {
            console.error("AI article generation failed:", error);
            showToast("Sorry, there was an error generating the article.", { type: 'error' });
        } finally {
            setIsGeneratingArticle(false);
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        
        const articleToSave = {
            ...article,
            slug: isEditing ? article.slug : generateSlug(article.title),
            published: !!article.published, // Ensure it's always a boolean
        };

        try {
            if (isEditing && id) {
                const articleRef = doc(db, 'articles', id);
                await updateDoc(articleRef, articleToSave);
                showToast('Article updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'articles'), articleToSave);
                showToast('Article created successfully!', { type: 'success' });
            }
            navigate('/admin/journal');
        } catch (error) {
            console.error("Error saving article:", error);
            showToast('Failed to save article. Please check the console.', { type: 'error' });
            setIsSaving(false);
        }
    };
    
    if (isLoading) {
        return <div className="text-center p-8">Loading article details...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Article' : 'Add New Article'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700">Article Title</label>
                            <input type="text" name="title" id="title" value={article.title} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                             {isEditing ? (
                                <p className="mt-1 text-xs text-gray-500 italic">Slug cannot be changed after creation: /journal/{article.slug}</p>
                            ) : (
                                article.slug && <p className="mt-1 text-xs text-gray-500 italic">Slug preview: /journal/{article.slug}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700">Excerpt (Short Summary)</label>
                            <textarea name="excerpt" id="excerpt" rows={3} value={article.excerpt} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"></textarea>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Generate Content with AI</h3>
                            <p className="text-sm text-gray-500 mb-2">Provide some keywords or an outline, and AI will write the full article for you.</p>
                            <div>
                                <label htmlFor="aiPrompt" className="block text-sm font-medium text-gray-700">Keywords / Outline</label>
                                <textarea
                                    name="aiPrompt"
                                    id="aiPrompt"
                                    rows={4}
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    placeholder="e.g., The importance of natural light in minimalist architecture. Discuss benefits like mood improvement, energy savings. Mention techniques like skylights, large windows, and open-plan layouts."
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                                />
                            </div>
                            <div className="mt-2 text-right">
                                <button
                                    type="button"
                                    onClick={handleGenerateArticle}
                                    disabled={isGeneratingArticle}
                                    className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400"
                                >
                                    <SparklesIcon className={`h-5 w-5 mr-2 ${isGeneratingArticle ? 'animate-pulse' : ''}`}/>
                                    {isGeneratingArticle ? 'Generating...' : 'Generate Full Article'}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700">Full Content</label>
                            <RichTextEditor 
                                value={article.content}
                                onChange={(value) => setArticle(prev => ({...prev, content: value}))}
                            />
                        </div>
                    </div>
                    <div className="space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <ImageUploader label="Feature Image" value={article.imageUrl} onChange={(val) => setArticle(p => ({...p, imageUrl: val}))}/>
                        
                        <div className="border-t pt-4">
                            <label htmlFor="imagePrompt" className="block text-sm font-medium text-gray-700">Or Generate with AI</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input 
                                    type="text" 
                                    id="imagePrompt" 
                                    value={imagePrompt} 
                                    onChange={e => setImagePrompt(e.target.value)} 
                                    placeholder="e.g., minimalist building with glass facade at sunset" 
                                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                                />
                                <button 
                                    type="button" 
                                    onClick={handleGenerateImage} 
                                    disabled={isGeneratingImage} 
                                    className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 flex items-center whitespace-nowrap text-sm"
                                >
                                    <SparklesIcon className={`h-4 w-4 mr-1 ${isGeneratingImage ? 'animate-pulse' : ''}`} />
                                    {isGeneratingImage ? '...' : 'Generate'}
                                </button>
                            </div>
                            {isGeneratingImage && <p className="text-xs text-center text-gray-500 mt-2">AI is creating your image. This may take a moment...</p>}
                            {generatedImage && (
                                <div className="mt-2 space-y-2">
                                    <img src={`data:image/jpeg;base64,${generatedImage}`} alt="AI generated preview" className="w-full h-auto rounded-md border" />
                                    <button 
                                        type="button" 
                                        onClick={handleUseGeneratedImage} 
                                        disabled={isUploadingGenerated} 
                                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 text-sm font-semibold"
                                    >
                                        {isUploadingGenerated ? 'Saving...' : 'Use This Image'}
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        <div>
                            <label htmlFor="author" className="block text-sm font-medium text-gray-700">Author</label>
                             <select
                                name="author"
                                id="author"
                                value={article.author.id}
                                onChange={handleAuthorChange}
                                required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
                            >
                                <option value="" disabled>Select a team member</option>
                                {teamMembers?.map(member => (
                                    <option key={member.id} value={member.id}>
                                        {member.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="publishDate" className="block text-sm font-medium text-gray-700">Publish Date</label>
                            <input type="date" name="publishDate" id="publishDate" value={article.publishDate} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                         <div className="flex items-start">
                           <div className="flex items-center h-5">
                             <input id="published" name="published" type="checkbox" checked={!!article.published} onChange={handleChange} className="h-4 w-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" />
                           </div>
                           <div className="ml-3 text-sm">
                             <label htmlFor="published" className="font-medium text-gray-700">Published</label>
                             <p className="text-gray-500">Uncheck to save as a draft.</p>
                           </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-4 bg-white p-4 rounded-lg shadow-md sticky bottom-4">
                    <button type="button" onClick={() => navigate('/admin/journal')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Article'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminArticleFormPage;